/**
 * [T157] TopMerchantsBarSkin — `top-merchants-bar` adapter.
 *
 * Resolver: topMerchantsList (PR2 stable).
 * @see https://recharts.org/en-US/api/BarChart
 * @see docs/design/T149-PR5-INSIGHTS-SKINS-SPEC.md §3.9
 */
import { Store } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, Cell, XAxis, YAxis } from 'recharts';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';
import { useWindowSize } from '@/hooks/useWindowSize';
import { useCurrency } from '@/hooks/useCurrency';
import { WidgetFrame } from '../../WidgetFrame';
import type { SkinProps } from '../types';
import {
  skinTestId,
  SkinLoadingRows,
  SkinLoadingBars,
  SkinEmptyState,
  SkinErrorState,
  INSIGHTS_PALETTE,
  useSkinFrameProps,
} from './_shared';

const MIN_W = 3;
const MIN_H = 3;

export default function TopMerchantsBarSkin(props: SkinProps<'top-merchants-bar'>) {
  const { widget, data, loading, error } = props;
  const { fmt } = useCurrency();
  const { width } = useWindowSize();
  const isMobile = (width ?? 1280) < 480;
  const isTooSmall = widget.w < MIN_W || widget.h < MIN_H;

  const merchants = (data?.merchants ?? [])
    .slice()
    .sort((a, b) => b.totalSpent - a.totalSpent)
    .slice(0, 10);
  const isEmpty = !loading && !error && merchants.length === 0;
  const top = merchants[0];

  const config: ChartConfig = {
    totalSpent: { label: 'Amount', color: 'hsl(var(--chart-2))' },
  };

  const chartData = merchants.map((m) => ({
    name: m.name,
    totalSpent: m.totalSpent,
    percentage: m.percentage,
  }));

  const frameProps = useSkinFrameProps(props);

  // Too-small fallback: top-merchant name + amount KPI
  if (isTooSmall) {
    return (
      <WidgetFrame
        id={widget.id}
        title={widget.title || 'Top Merchants'}
        icon={<Store className="h-4 w-4" />}
        {...frameProps}
        body={
          <div className="flex flex-col justify-center h-full gap-1 py-1">
            {loading ? (
              <SkinLoadingRows rows={2} />
            ) : top ? (
              <>
                <p className="text-xs text-muted-foreground truncate">{top.name}</p>
                <p className="text-lg font-bold truncate">{fmt(top.totalSpent)}</p>
              </>
            ) : (
              <p className="text-xs text-muted-foreground">No data</p>
            )}
          </div>
        }
      />
    );
  }

  // Mobile fallback: top-5 merchants list
  if (isMobile) {
    return (
      <WidgetFrame
        id={widget.id}
        title={widget.title || 'Top Merchants'}
        icon={<Store className="h-4 w-4" />}
        {...frameProps}
        body={
          loading ? (
            <SkinLoadingRows rows={5} />
          ) : isEmpty ? (
            <div data-testid={skinTestId('top-merchants-bar', widget.id, 'empty')}>
              <SkinEmptyState
                title="No merchant data for this period"
                description="Add transactions with merchant names to see top merchants."
                cta={{ label: 'Add transaction', to: '/transactions' }}
              />
            </div>
          ) : (
            <ul className="space-y-1 w-full" role="list" aria-label="Top merchants">
              {merchants.slice(0, 5).map((m, i) => (
                <li key={m.name} className="flex items-center gap-2 min-w-0 py-1.5 text-xs">
                  <span
                    className="shrink-0 w-2 h-2 rounded-full"
                    style={{ background: INSIGHTS_PALETTE[i % INSIGHTS_PALETTE.length] }}
                    aria-hidden="true"
                  />
                  <span className="break-words flex-1 min-w-0">{m.name}</span>
                  <span className="shrink-0 font-medium">{fmt(m.totalSpent)}</span>
                </li>
              ))}
            </ul>
          )
        }
      />
    );
  }

  // Main render
  return (
    <WidgetFrame
      id={widget.id}
      title={widget.title || 'Top Merchants'}
      icon={<Store className="h-4 w-4" />}
      {...frameProps}
      body={
        loading ? (
          <div data-testid={skinTestId('top-merchants-bar', widget.id, 'loading')}>
            <SkinLoadingBars bars={5} />
          </div>
        ) : error ? (
          <div data-testid={skinTestId('top-merchants-bar', widget.id, 'error')}>
            <SkinErrorState message={error.message} />
          </div>
        ) : isEmpty ? (
          <div data-testid={skinTestId('top-merchants-bar', widget.id, 'empty')}>
            <SkinEmptyState
              title="No merchant data for this period"
              description="Add transactions with merchant names to see top merchants."
              cta={{ label: 'Add transaction', to: '/transactions' }}
            />
          </div>
        ) : (
          <div
            role="img"
            aria-label={`Top merchants: ${merchants.length} merchants`}
            className="w-full h-full min-w-0 min-h-0 overflow-hidden"
            data-testid={skinTestId('top-merchants-bar', widget.id)}
          >
            <ChartContainer config={config} className="h-full w-full overflow-hidden">
              <BarChart
                data={chartData}
                layout="vertical"
                margin={{ left: 4, right: 40, top: 4, bottom: 4 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  className="stroke-border/60"
                  horizontal={false}
                />
                <XAxis
                  type="number"
                  tickFormatter={(v) =>
                    v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v)
                  }
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: 10 }}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  tickLine={false}
                  axisLine={false}
                  width={100}
                  tick={{ fontSize: 10 }}
                  tickFormatter={(v: string) =>
                    v.length > 14 ? `${v.slice(0, 12)}\u2026` : v
                  }
                />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Bar dataKey="totalSpent" barSize={16} radius={[0, 4, 4, 0]}>
                  {chartData.map((_, i) => (
                    <Cell key={i} fill={INSIGHTS_PALETTE[i % INSIGHTS_PALETTE.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ChartContainer>
          </div>
        )
      }
    />
  );
}
